import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Spinner, Tooltip } from '@fluentui/react-components';
import { amountKeysFor } from '../../services/complianceApi';
import type { SourceStatement } from '../../types';
import { formatCurrency } from '../../utils/complianceFormatters';
import { findLikelyDuplicates } from '../../utils/statementOverlap';
import { acceptFor, SOURCE_RULES, validateFile } from '../upload/uploadRules';

/** Source types the extractor can read: PDF, images (gpt-4o vision), and spreadsheets. Equal to the server's list. */
const ACCEPT = acceptFor(SOURCE_RULES);

/** Auto-collapse threshold — more statements than this collapses the list to a summary line. */
const COLLAPSE_THRESHOLD = 3;

/** One in-flight (or failed) upload row. `error` set → inline error row instead of a spinner. */
export interface SourcesUploadingFile {
  /** Per-batch unique row id (e.g. `${batchId}-${index}`) — NOT the filename. Two files
   *  with the same name in one batch must keep distinct rows and per-row attribution. */
  id: string;
  fileName: string;
  error?: string;
}

export interface SourcesBlockProps {
  taskId: string;
  clientId: string;
  obligationId: string;
  obligationType: string;
  /** Statements already on file for this (task, obligation). */
  statements: SourceStatement[];
  /** Filing finalized — rows stay visible, ✕ and the drop zone are disabled. */
  finalized: boolean;
  /** Reconciliation/cross-check warning — swaps the header ✓ for ⚠. */
  warning?: string;
  /**
   * Headline figure key shown per row and summed in the header.
   * Default: sales_tax → 'taxCollected'; other types → first amountKeysFor() entry.
   */
  primaryFigureKey?: string;
  /** Multi-file drop / pick handler — the parent fans these out to sequential ingests. */
  onIngestFiles: (files: File[]) => void;
  /** Id-based statement remove (statements without ids — legacy — get no ✕). */
  onRemove: (statementId: string) => void;
  /** Statement ids with an in-flight remove — their ✕ is disabled. */
  busyStatementIds: string[];
  /** Per-file progress rows for the current upload batch. */
  uploadingFiles: SourcesUploadingFile[];
}

const breakdownCellStyle: React.CSSProperties = {
  border: '1px solid var(--colorNeutralStroke2, #e0e0e0)',
  padding: '2px 8px',
};

const breakdownHeaderCellStyle: React.CSSProperties = {
  ...breakdownCellStyle,
  textAlign: 'left',
  fontWeight: 'var(--fontWeightSemibold)' as never,
  color: 'var(--colorNeutralForeground2)',
};

/** Screen-reader-only text (the visual cell shows a bare em dash). */
const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** Type icon by filename: 📄 pdf (default) / 🖼 image / 📊 sheet. */
function fileIcon(fileName?: string): string {
  const ext = (fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return '🖼';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return '📊';
  return '📄';
}

export default function SourcesBlock({
  obligationId,
  obligationType,
  statements,
  finalized,
  warning,
  primaryFigureKey,
  onIngestFiles,
  onRemove,
  busyStatementIds,
  uploadingFiles,
}: SourcesBlockProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // Files refused before anything was sent (U3): each names the file and why. Cleared by the next add.
  const [refused, setRefused] = useState<string[]>([]);
  // null = follow the auto rule (expanded while N ≤ threshold); true/false = user toggled.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? statements.length <= COLLAPSE_THRESHOLD;
  // Per-document attribution matrix — collapsed by default, only meaningful with ≥2 docs.
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const figureKey =
    primaryFigureKey ??
    (obligationType === 'sales_tax' ? 'taxCollected' : amountKeysFor(obligationType)[0]);

  const summed = statements.reduce((acc, s) => acc + (s.figures?.[figureKey] ?? 0), 0);

  // Union of figure keys across all statements, ordered by the form's known amount
  // keys first (unknown extras keep first-seen order at the end).
  const breakdownKeys = useMemo(() => {
    const seen: string[] = [];
    for (const s of statements) {
      for (const k of Object.keys(s.figures ?? {})) {
        if (!seen.includes(k)) seen.push(k);
      }
    }
    const known = amountKeysFor(obligationType);
    const rank = (k: string) => {
      const i = known.indexOf(k);
      return i === -1 ? known.length : i;
    };
    return seen.sort((a, b) => rank(a) - rank(b));
  }, [statements, obligationType]);

  const showBreakdown = statements.length >= 2;

  // Conversion fix #3: say when two statements look like the SAME sales. The sum is unchanged (a
  // client can genuinely have several reports); this only stops a doubled figure passing silently.
  const overlaps = useMemo(() => findLikelyDuplicates(statements), [statements]);
  const nameOf = (st: SourceStatement) => st.sourceFileName ?? st.label ?? `#${st.slot}`;

  function openPicker() {
    if (!finalized) inputRef.current?.click();
  }

  // Every add, dropped or picked, is validated first. Only the files the server will take are sent.
  function take(files: File[]) {
    if (finalized || files.length === 0) return;
    const ok: File[] = [];
    const bad: string[] = [];
    for (const file of files) {
      const why = validateFile(file, SOURCE_RULES);
      if (why) bad.push(t('upload.refusedFile', { name: file.name, reason: why }));
      else ok.push(file);
    }
    setRefused(bad);
    if (ok.length > 0) onIngestFiles(ok);
  }

  function handlePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    take(files);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    take(Array.from(e.dataTransfer?.files ?? []));
  }

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '2px 0',
    fontSize: 'var(--fontSizeBase200)',
  };

  return (
    <div>
      {/* Hidden multi-file input — shared by the drop zone click and the [+] quick-add. */}
      <input
        ref={inputRef}
        data-testid={`source-file-input-${obligationId}`}
        type="file"
        accept={ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={handlePicked}
      />

      {statements.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            data-testid={`sources-toggle-${obligationId}`}
            aria-expanded={expanded}
            onClick={() => setUserExpanded(!expanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '2px 0',
              fontSize: 'var(--fontSizeBase200)',
              color: 'var(--colorNeutralForeground2)',
            }}
          >
            <span>{expanded ? '▾' : '▸'}</span>
            <span style={{ fontWeight: 'var(--fontWeightSemibold)' as never }}>
              {t('sourcesBlock.header', { count: statements.length })}
            </span>
            <span>{' · '}</span>
            <span>{t('sourcesBlock.summed', { amount: formatCurrency(summed) })}</span>
            <span>{warning ? '⚠' : '✓'}</span>
          </button>
          {showBreakdown && (
            <Button
              size="small"
              appearance="transparent"
              data-testid={`sources-breakdown-toggle-${obligationId}`}
              aria-expanded={breakdownOpen}
              onClick={() => setBreakdownOpen((v) => !v)}
            >
              {breakdownOpen
                ? t('sourcesBlock.breakdown.hideBreakdown')
                : t('sourcesBlock.breakdown.viewBreakdown')}
            </Button>
          )}
          {!expanded && !finalized && (
            <Button size="small" appearance="subtle" onClick={openPicker}>
              {`[+] ${t('sourcesBlock.addFiles')}`}
            </Button>
          )}
        </div>
      )}

      {overlaps.length > 0 && (
        <div
          role="status"
          data-testid={`sources-overlap-${obligationId}`}
          style={{
            margin: '4px 0',
            padding: '6px 8px',
            borderRadius: 4,
            fontSize: 'var(--fontSizeBase200)',
            background: 'var(--colorPaletteYellowBackground1)',
            color: 'var(--colorNeutralForeground1)',
            border: '1px solid var(--colorPaletteYellowBorder1)',
          }}
        >
          {overlaps.map((o) => (
            <div key={`${nameOf(o.a)}|${nameOf(o.b)}`}>
              {'⚠ '}
              {o.reason === 'sameName'
                ? t('sourcesBlock.overlap.sameName', { name: nameOf(o.a) })
                : t('sourcesBlock.overlap.similarGross', {
                  a: nameOf(o.a),
                  b: nameOf(o.b),
                  ga: formatCurrency(o.a.figures?.grossSales ?? null),
                  gb: formatCurrency(o.b.figures?.grossSales ?? null),
                })}
            </div>
          ))}
        </div>
      )}

      {expanded &&
        statements.map((s) => {
          const rowKey = s.id ?? `slot-${s.slot}`;
          const busy = !!s.id && busyStatementIds.includes(s.id);
          const lowConfidence = s.confidence != null && s.confidence < 0.5;
          return (
            <div key={rowKey} data-testid={`source-row-${rowKey}`} style={rowStyle}>
              <span>{fileIcon(s.sourceFileName)}</span>
              <span
                style={{
                  maxWidth: '22rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.sourceFileName ?? s.label ?? `#${s.slot}`}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(s.figures?.[figureKey] ?? null)}
              </span>
              {lowConfidence && (
                <Badge appearance="tint" color="warning" size="small">
                  {t('sourcesBlock.lowConfidence')}
                </Badge>
              )}
              {!finalized && s.id && (
                <Button
                  size="small"
                  appearance="subtle"
                  disabled={busy}
                  icon={busy ? <Spinner size="tiny" /> : undefined}
                  aria-label={t('sourcesBlock.removeSource', { name: s.sourceFileName ?? s.slot })}
                  onClick={() => onRemove(s.id!)}
                >
                  ✕
                </Button>
              )}
            </div>
          );
        })}

      {/* Per-document attribution matrix: which uploaded document contributed what to each
          computed/summed figure. Errors in a total can be traced to a single document. */}
      {showBreakdown && breakdownOpen && (
        <div style={{ overflowX: 'auto', marginTop: 4 }}>
          <table
            data-testid={`sources-breakdown-${obligationId}`}
            style={{
              borderCollapse: 'collapse',
              fontSize: 'var(--fontSizeBase200)',
              whiteSpace: 'nowrap',
            }}
          >
            <thead>
              <tr>
                <th style={breakdownHeaderCellStyle}>{t('sourcesBlock.breakdown.figure')}</th>
                {statements.map((s) => {
                  const colKey = s.id ?? `slot-${s.slot}`;
                  const name = s.sourceFileName ?? s.label ?? `#${s.slot}`;
                  return (
                    <th key={colKey} style={{ ...breakdownHeaderCellStyle, textAlign: 'right' }}>
                      <Tooltip content={name} relationship="description">
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '9rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                          }}
                        >
                          {name}
                        </span>
                      </Tooltip>
                    </th>
                  );
                })}
                <th style={{ ...breakdownHeaderCellStyle, textAlign: 'right' }}>
                  {t('sourcesBlock.breakdown.total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {breakdownKeys.map((key) => {
                const total = statements.reduce((acc, s) => acc + (s.figures?.[key] ?? 0), 0);
                return (
                  <tr key={key}>
                    <td style={breakdownCellStyle}>{t(`amounts.${key}`, { defaultValue: key })}</td>
                    {statements.map((s) => {
                      const colKey = s.id ?? `slot-${s.slot}`;
                      const value = s.figures?.[key] ?? null;
                      const lowConfidence = s.confidence != null && s.confidence < 0.5;
                      return (
                        <td
                          key={colKey}
                          data-testid={`breakdown-cell-${key}-${colKey}`}
                          data-low-confidence={lowConfidence ? 'true' : undefined}
                          style={{
                            ...breakdownCellStyle,
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            ...(lowConfidence
                              ? {
                                  background: 'var(--colorStatusWarningBackground1, #fff9f5)',
                                  color: 'var(--colorStatusWarningForeground1, #bc4b09)',
                                }
                              : undefined),
                          }}
                        >
                          {value == null ? (
                            <span aria-hidden="true">—</span>
                          ) : (
                            formatCurrency(value)
                          )}
                          {value == null && (
                            <span style={visuallyHiddenStyle}>
                              {t('sourcesBlock.breakdown.notInDocument')}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td
                      data-testid={`breakdown-total-${key}`}
                      style={{
                        ...breakdownCellStyle,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 'var(--fontWeightSemibold)' as never,
                      }}
                    >
                      {formatCurrency(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-file upload progress: spinner rows while in flight, inline error rows on failure.
          Keyed by the per-batch row id — filenames can repeat within a batch. */}
      {uploadingFiles.map((u) =>
        u.error ? (
          <div key={u.id} data-testid={`source-uploading-${u.id}`} style={rowStyle}>
            <span>{fileIcon(u.fileName)}</span>
            <span>{u.fileName}</span>
            <span style={{ color: 'var(--colorPaletteRedForeground1, #b10e1c)' }}>{u.error}</span>
          </div>
        ) : (
          <div key={u.id} data-testid={`source-uploading-${u.id}`} style={rowStyle}>
            <Spinner size="tiny" />
            <span>{t('sourcesBlock.uploading', { name: u.fileName })}</span>
          </div>
        ),
      )}

      {refused.map((line, i) => (
        <div key={i} data-testid={`source-refused-${i}`} style={{ ...rowStyle, color: 'var(--colorPaletteRedForeground1, #b10e1c)' }}>
          {line}
        </div>
      ))}

      {/* Drop zone — hidden while collapsed (the [+] quick-add covers adds) and never
          interactive when finalized. */}
      {expanded && (
        <div
          data-testid={`source-dropzone-${obligationId}`}
          onDragOver={(e) => { e.preventDefault(); if (!finalized) setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); if (!finalized) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={openPicker}
          style={{
            border: `1px dashed ${dragOver ? 'var(--stx-dropzone-border-active)' : 'var(--colorNeutralStroke2)'}`,
            background: dragOver ? 'var(--colorNeutralBackground1Selected, #eef6e8)' : 'transparent',
            borderRadius: 6,
            padding: 8,
            marginTop: 4,
            fontSize: 'var(--fontSizeBase200)',
            color: 'var(--colorNeutralForeground3)',
            cursor: finalized ? 'default' : 'pointer',
            opacity: finalized ? 0.6 : 1,
            transition: 'border-color 120ms, background 120ms',
          }}
        >
          {t('sourcesBlock.addFiles')}
          {/* U1: the zone is a div, which is not a control. This button is the keyboard path to the same picker. */}
          {!finalized && (
            <Button
              size="small"
              appearance="secondary"
              style={{ marginLeft: 8 }}
              onClick={(e) => { e.stopPropagation(); openPicker(); }}
            >
              {t('upload.choose')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
